/**
 * Wallet crypto — AES-GCM encryption + PBKDF2 key derivation via Web Crypto.
 *
 * Security parameters:
 * - PBKDF2: 100,000 iterations, SHA-256, 256-bit derived key
 * - AES-GCM: 256-bit key, 96-bit IV (randomly generated per encryption)
 * - Salt: 16 bytes, randomly generated per wallet creation
 */

const PBKDF2_ITERATIONS = 100_000;
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits for AES-GCM
const SALT_LENGTH = 16;

export interface EncryptionResult {
  ciphertext: ArrayBuffer;
  iv: Uint8Array;
  salt: Uint8Array;
}

/**
 * Derive an AES-256-GCM key from a password using PBKDF2.
 */
async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  const baseKey = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt plaintext with AES-GCM using a password-derived key.
 * Returns ciphertext + IV + salt needed for decryption.
 */
export async function encrypt(plaintext: string, password: string): Promise<EncryptionResult> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plaintext);

  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const key = await deriveKey(password, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    data
  );

  return { ciphertext, iv, salt };
}

/**
 * Decrypt ciphertext with AES-GCM using the same password + salt + IV.
 * Throws on wrong password (AES-GCM authentication failure).
 */
export async function decrypt(
  ciphertext: ArrayBuffer,
  password: string,
  iv: Uint8Array,
  salt: Uint8Array
): Promise<string> {
  const key = await deriveKey(password, salt);

  let plainBuffer: ArrayBuffer;
  try {
    plainBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      key,
      ciphertext
    );
  } catch {
    throw new WalletCryptoError(
      'DECRYPT_FAILED',
      'Decryption failed. Wrong password or corrupted data.'
    );
  }

  const decoder = new TextDecoder();
  return decoder.decode(plainBuffer);
}

/**
 * Compute password strength score (0-4).
 * 0: very weak, 1: weak, 2: fair, 3: strong, 4: very strong
 */
export function passwordStrength(password: string): {
  score: number;
  label: string;
  feedback: string;
} {
  let score = 0;

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  score = Math.min(score, 4);

  const labels = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];
  const feedbacks = [
    'Use at least 8 characters',
    'Add uppercase, numbers, or symbols',
    'Consider making it longer',
    'Good password',
    'Excellent password',
  ];

  return {
    score,
    label: labels[score] ?? 'Unknown',
    feedback: feedbacks[score] ?? '',
  };
}

export class WalletCryptoError extends Error {
  constructor(
    public readonly code: 'DECRYPT_FAILED' | 'ENCRYPT_FAILED',
    message: string
  ) {
    super(message);
    this.name = 'WalletCryptoError';
  }
}
